import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, HelpCircle, Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Link } from 'react-router-dom';
import { SpotlightCard } from './ui/SpotlightCard';
import { FEATURE_LABELS, formatLimitsAsFeatures, type PlanLimits } from '../../../lib/pricing-constants';

// Types for the API response
interface PublicPlan {
  public_id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  currency: string;
  limits: PlanLimits;
  features: string[];
  trial_days: number;
  is_featured: boolean;
  sort_order: number;
}

// Format price for display (no decimals)
function formatPrice(price: number, currency: string): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

export function DynamicPricing({ t, lang = 'fr' }: { t: any; lang?: string }) {
  const [frequency, setFrequency] = useState<'monthly' | 'annually'>('monthly');
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/billing/plans');
        if (!response.ok) throw new Error('Failed to fetch plans');
        const data = await response.json();
        // API returns {plans: [...]} or [...] depending on endpoint
        const plansArray = Array.isArray(data) ? data : (data.plans || []);
        setPlans(plansArray);
        setError(null);
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError('Unable to load pricing plans');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Calculate max savings percentage across all plans
  const maxSavingsPercent = plans.reduce((max, plan) => {
    if (plan.price_monthly <= 0 || !plan.price_yearly) return max;
    const monthlyTotal = plan.price_monthly * 12;
    const savings = ((monthlyTotal - plan.price_yearly) / monthlyTotal) * 100;
    return Math.max(max, Math.round(savings));
  }, 0);

  const annualLabel = maxSavingsPercent > 0 
    ? `${t.pricing.annually} (-${maxSavingsPercent}%)`
    : t.pricing.annually;

  const frequencies = [
    { value: 'monthly' as const, label: t.pricing.monthly, priceSuffix: t.pricing.suffix },
    { value: 'annually' as const, label: annualLabel, priceSuffix: t.pricing.suffix },
  ];

  // Transform API plans to display format
  const displayPlans = plans.map(plan => {
    const features = Array.isArray(plan.features) ? plan.features : [];
    const featureLabels = features.map(f => FEATURE_LABELS[lang]?.[f] || f);
    
    // Calculate monthly equivalent for annual pricing
    const yearlyTotal = plan.price_yearly || plan.price_monthly * 10;
    const monthlyEquivalent = yearlyTotal / 12;
    const savingsPercent = plan.price_monthly > 0 && plan.price_yearly
      ? Math.round(((plan.price_monthly * 12) - plan.price_yearly) / (plan.price_monthly * 12) * 100)
      : 0;
    
    return {
      id: `tier-${plan.slug}`,
      name: plan.name,
      desc: plan.description || '',
      href: plan.price_monthly === 0 ? '/register' : `/checkout?plan=${plan.slug}&interval=${frequency === 'annually' ? 'year' : 'month'}`,
      price: {
        monthly: formatPrice(plan.price_monthly, plan.currency),
        annually: formatPrice(monthlyEquivalent, plan.currency),
        yearlyTotal: formatPrice(yearlyTotal, plan.currency),
        savingsPercent,
      },
      isFree: plan.price_monthly === 0,
      limits: plan.limits,
      features: featureLabels,
      featured: plan.is_featured,
      cta: plan.price_monthly === 0 
        ? (lang === 'fr' ? 'Commencer gratuitement' : 'Start for free')
        : (lang === 'fr' ? 'Commencer' : 'Get started'),
      footer: plan.price_monthly > 0 ? t.pricing.footer_overage : null,
      trial_days: plan.trial_days,
    };
  });

  if (loading) {
    return (
      <div id="pricing" className="bg-zinc-950 py-24 sm:py-32 relative">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-base font-semibold leading-7 text-emerald-400">{t.pricing.section_title}</h2>
            <p className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              {t.pricing.main_title}
            </p>
          </div>
          <div className="mt-16 flex justify-center">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (error || displayPlans.length === 0) {
    // Fallback: don't render anything if no plans
    return null;
  }

  // Grid columns based on number of plans
  const gridCols = displayPlans.length <= 2 
    ? 'lg:grid-cols-2' 
    : displayPlans.length === 3 
      ? 'lg:grid-cols-3' 
      : 'lg:grid-cols-4';

  return (
    <div id="pricing" className="bg-zinc-950 py-24 sm:py-32 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none" />
      
      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base font-semibold leading-7 text-emerald-400">{t.pricing.section_title}</h2>
            <p className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              {t.pricing.main_title}
            </p>
          </motion.div>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mt-16 flex justify-center"
        >
          <div className="relative flex rounded-full bg-white/5 p-1 ring-1 ring-white/10">
            {frequencies.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFrequency(option.value)}
                className={cn(
                  frequency === option.value ? 'bg-emerald-500 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-200',
                  'relative rounded-full px-6 py-2 text-sm font-semibold leading-5 transition-all'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
          {maxSavingsPercent > 0 && (
            <span className="ml-4 self-center text-xs font-medium text-emerald-400">
              {lang === 'fr' 
                ? `Économisez ~${maxSavingsPercent}% avec la facturation annuelle`
                : lang === 'es'
                  ? `Ahorre ~${maxSavingsPercent}% con facturación anual`
                  : `Save ~${maxSavingsPercent}% with annual billing`}
            </span>
          )}
        </motion.div>

        <div className={cn("isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none", gridCols)}>
          {displayPlans.map((tier, idx) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -8 }}
              className="h-full relative"
            >
              {tier.featured && (
                <span className="absolute -top-3 right-6 z-10 inline-flex items-center rounded-full bg-emerald-500 px-3 py-1 text-xs font-medium text-white shadow-sm ring-4 ring-zinc-950">
                  {lang === 'fr' ? 'POPULAIRE' : 'POPULAR'}
                </span>
              )}
              <SpotlightCard
                className={cn(
                  "h-full p-8 xl:p-6 flex flex-col justify-between",
                  tier.featured ? 'ring-2 ring-emerald-500 bg-zinc-900/50' : ''
                )}
                spotlightColor={tier.featured ? "rgba(16, 185, 129, 0.2)" : "rgba(255, 255, 255, 0.1)"}
              >
                <div>
                  <div className="flex items-center justify-between gap-x-4">
                    <h3 id={tier.id} className={cn("text-lg font-semibold leading-8", tier.featured ? "text-emerald-400" : "text-white")}>
                      {tier.name}
                    </h3>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-zinc-400 min-h-[48px]">{tier.desc}</p>
                  <p className="mt-6 flex items-baseline gap-x-1">
                    <span className="text-4xl font-bold tracking-tight text-white">
                      {frequency === 'annually' ? tier.price.annually : tier.price.monthly}
                    </span>
                    <span className="text-sm font-semibold leading-6 text-zinc-400">
                      {frequencies.find(f => f.value === frequency)?.priceSuffix}
                    </span>
                  </p>
                  {/* Annual billing annotation */}
                  {frequency === 'annually' && !tier.isFree && tier.price.savingsPercent > 0 && (
                    <p className="mt-2 text-xs font-medium text-emerald-400">
                      {tier.price.yearlyTotal} {lang === 'fr' ? 'facturé/an' : 'billed/year'} — {lang === 'fr' ? 'économie de' : 'save'} {tier.price.savingsPercent}%
                    </p>
                  )}
                  {tier.trial_days > 0 && (
                    <p className="mt-2 text-xs text-emerald-400">
                      {lang === 'fr' ? `${tier.trial_days} jours d'essai gratuit` : `${tier.trial_days} days free trial`}
                    </p>
                  )}
                  {/* Limits as table */}
                  <div className="mt-6 space-y-2 text-sm text-zinc-300">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">{lang === 'fr' ? 'Agents' : 'Agents'}</span>
                      <span className="font-semibold text-white">
                        {tier.limits.max_agents === -1 ? (lang === 'fr' ? 'Illimité' : 'Unlimited') : tier.limits.max_agents}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">{lang === 'fr' ? 'Conversations/mois' : 'Conversations/month'}</span>
                      <span className="font-semibold text-white">
                        {tier.limits.max_conversations_per_month === -1 
                          ? (lang === 'fr' ? 'Illimité' : 'Unlimited')
                          : tier.limits.max_conversations_per_month.toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">{lang === 'fr' ? 'Documents' : 'Documents'}</span>
                      <span className="font-semibold text-white">
                        {tier.limits.max_knowledge_items === -1 
                          ? (lang === 'fr' ? 'Illimité' : 'Unlimited')
                          : tier.limits.max_knowledge_items.toLocaleString('fr-FR')}
                      </span>
                    </div>
                    {tier.limits.max_storage_mb != null && (
                      <div className="flex justify-between">
                        <span className="text-zinc-400">{lang === 'fr' ? 'Stockage' : 'Storage'}</span>
                        <span className="font-semibold text-white">
                          {tier.limits.max_storage_mb === -1 
                            ? (lang === 'fr' ? 'Illimité' : 'Unlimited')
                            : `${tier.limits.max_storage_mb} Mo`}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Features list */}
                  <ul role="list" className="mt-6 space-y-3 text-sm leading-6 text-zinc-300">
                    {tier.features.map((feature: string) => (
                      <li key={feature} className="flex gap-x-3">
                        <Check className={cn("h-6 w-5 flex-none", tier.featured ? "text-emerald-400" : "text-zinc-500")} aria-hidden="true" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {tier.footer && (
                     <div className="mt-4 pt-4 border-t border-white/5 text-xs text-zinc-500 flex items-start gap-2">
                        <HelpCircle className="w-3 h-3 mt-0.5" />
                        {tier.footer}
                     </div>
                  )}
                </div>
                <Link
                  to={tier.href}
                  aria-describedby={tier.id}
                  className={cn(
                    tier.featured
                      ? 'bg-emerald-500 text-white shadow-sm hover:bg-emerald-400 shadow-emerald-500/20'
                      : 'bg-white/10 text-white hover:bg-white/20',
                    'mt-8 block rounded-xl py-2.5 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500 transition-all hover:scale-105'
                  )}
                >
                  {tier.cta}
                </Link>
              </SpotlightCard>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
